/* eslint-disable prefer-arrow/prefer-arrow-functions */
import { ContentRatingApiModel, LocalizedStringApiModel, MediaApiModel } from '@cxb/content-stb-api';
import { AgeRate, LocalizedData, MediaCategory } from '../../../common';
import { Content } from '../../../domain';
import { MpxContentResponse, MpxMedia } from '../../../mpx';
import { ContentViewModel } from '../../components/carousel-item/content-view.model';
import { throwError } from '../../utils';
import {
  ProgramAvailabilityLocalizedObject,
  ProgramAvailabilityMedia,
  ProgramAvailabilityMediaCategory,
  ProgramAvailabilityRating,
  ProgramAvailabilityTag,
  ProgramAvailabilityThumbnails,
} from '../mpx';

interface Thumbnail {
  landscapeUrl: string;
  portraitUrl: string;
}

export function mapMpxLogoToCarouselBackgroundImageUrl(thumbnails: ProgramAvailabilityThumbnails, cleanBackgroundNeeded: boolean): string {
  return (
    Object.entries(thumbnails).find(([key, value]) =>
      cleanBackgroundNeeded ? key.includes('LANDSCAPE-CL') : key.includes('LANDSCAPE-TT')
    )?.[1].url ??
    Object.values(thumbnails)[0]?.url ??
    ''
  );
}

export function mapMpxLogoToCarouselTitleTreatmentUrl(thumbnails: ProgramAvailabilityThumbnails): string {
  let result = '';
  const thumbnailEntries = Object.entries(thumbnails);
  const logoEntries = thumbnailEntries.filter((entry) => entry[0].toLowerCase().startsWith('logo'));
  if (logoEntries.length > 0) {
    result = logoEntries[0][1].url;
  }
  return result;
}

export function mapMpxTagsToGenres(tags: ProgramAvailabilityTag[], engTags?: ProgramAvailabilityTag[]): LocalizedData<string>[] {
  return tags
    ?.filter((tag) => tag.scheme === 'genre')
    ?.map((tag, i) => ({
      ar: tag.titleLocalized.ar || tag.title,
      en: (engTags ? engTags[i].title : tag.titleLocalized.en) || tag.title,
    }));
}

export function mapMpxIdNumberFromUrl(id: string): string {
  if (id) {
    const splitId = id.split('/');
    return splitId[splitId.length - 1];
  }
  return '';
}

export function mapMpxMediaToCarouselVideoUrl(media: Array<ProgramAvailabilityMedia | MediaApiModel | MpxMedia>): string {
  const notTrailerVideo = getMpxMedium(media, false);
  return notTrailerVideo ? notTrailerVideo.publicUrl : undefined;
}

export function mapMpxMediaToCarouselTrailerUrl(media: Array<ProgramAvailabilityMedia | MediaApiModel | MpxMedia>): string {
  const trailer = this.getMpxMedium(media, true);
  return trailer ? trailer.publicUrl : undefined;
}

export function getMpxMedium(
  media: Array<ProgramAvailabilityMedia | MediaApiModel | MpxMedia>,
  isTrailer
): ProgramAvailabilityMedia | MediaApiModel | MpxMedia | undefined {
  return media?.find((medium) => {
    if (!medium.categories) {
      console.warn(`Missing categories field from model: ${medium.title} - ${medium.pid} - ${medium.id}`);
      return false;
    } else {
      const isTrailerMedium = getMediumCategory(medium.categories, MediaCategory.PREVIEW.toString());
      return isTrailer ? isTrailerMedium : !isTrailerMedium;
    }
  });
}

export function mapMpxThumbnailToCarouselThumbnail(thumbnails: ProgramAvailabilityThumbnails): Thumbnail {
  const result: Thumbnail = { landscapeUrl: '', portraitUrl: '' };
  const thumbnailEntries = Object.entries(thumbnails);
  const landScapeEntries = thumbnailEntries.filter((entry) => entry[0].toLowerCase().startsWith('landscape'));
  if (landScapeEntries.length > 0) {
    result.landscapeUrl = landScapeEntries[0][1].url;
  }
  const landScapeCleanEntries = thumbnailEntries.filter((entry) => entry[0].toLowerCase().startsWith('landscape-tt'));
  if (landScapeCleanEntries.length > 0 && landScapeCleanEntries[0][1].url) {
    result.landscapeUrl = landScapeCleanEntries[0][1].url;
  }
  const portraitEntries = thumbnailEntries.filter((entry) => entry[0].toLowerCase().startsWith('portrait'));
  if (portraitEntries.length > 0) {
    result.portraitUrl = portraitEntries[0][1].url;
  }
  return result;
}

export function mapMpxRatingsToAgeRate(ratings: ProgramAvailabilityRating[] | ContentRatingApiModel[]): AgeRate {
  const rating = ratings?.[0]?.rating?.toUpperCase();
  if (!rating) {
    return;
  }
  switch (rating) {
    case AgeRate.Eighteen:
      return AgeRate.Eighteen;
    case AgeRate.EighteenR:
      return AgeRate.EighteenR;
    case AgeRate.FAMILY:
      return AgeRate.FAMILY;
    case AgeRate.Fifteen:
      return AgeRate.Fifteen;
    case AgeRate.PG:
      return AgeRate.PG;
    case AgeRate.PG15:
      return AgeRate.PG15;
    case AgeRate.R:
      return AgeRate.R;
    case AgeRate.Sixteen:
      return AgeRate.Sixteen;
    case AgeRate.SixteenD:
      return AgeRate.SixteenD;
  }
}

export function getMediumCategory(
  categories: Array<ProgramAvailabilityMediaCategory | string>,
  categoryString: string
): ProgramAvailabilityMediaCategory | string | undefined {
  return categories.find((category) => {
    if ((category as ProgramAvailabilityMediaCategory)?.name) {
      return (category as ProgramAvailabilityMediaCategory).name === categoryString;
    }
    return category === categoryString;
  });
}

export function mapMpxMediaTrailer(media: ProgramAvailabilityMedia[]): ProgramAvailabilityMedia | undefined {
  return media?.find((medium) => getMediumCategory(medium.categories, MediaCategory.PREVIEW.toString()));
}

export function mapContentToContentViewModel(content: Content): ContentViewModel {
  return {
    id: content.id,
    guid: content.guid,
    title: content.title,
    type: content.type,
  };
}

export function mapMpxContentToContent(content: MpxContentResponse): Content {
  return {
    id: content.id,
    guid: content.guid,
    type: content.programType,
    title: content.titleLocalized,
  };
}

export function mapMpxTagsToBrand(tags: ProgramAvailabilityTag[]): string {
  return tags?.filter((tag) => tag.scheme === 'brand')?.map((tag) => tag.titleLocalized.en || tag.title)?.[0] ?? '';
}

export function mapLocalizedFields(
  localizedObject: ProgramAvailabilityLocalizedObject | LocalizedStringApiModel,
  fallback: string
): LocalizedData<string> {
  return {
    ar: localizedObject?.ar || fallback,
    en: localizedObject?.en || fallback,
  };
}

export function mapMpxItemToContent(mpxItem: {
  type?: string;
  programType?: string;
  contentType?: string;
  id: string;
  guid: string;
  title: Partial<LocalizedData<string>>;
}): Content {
  const type =
    mpxItem.type ?? mpxItem.programType ?? mpxItem.contentType ?? throwError('Failed to map MPX item, it has no recognisable type');

  return {
    type: type === 'movie' ? 'movie' : type === 'series' ? 'series' : type === 'episode' ? 'episode' : 'season',
    guid: mpxItem.guid,
    id: mpxItem.id,
    title: {
      en: mpxItem.title.en ?? '',
      ar: mpxItem.title.ar ?? '',
    },
  };
}
